/**
 * Encounter status — the domain state machine for a clinical visit (`docs/roadmap.md` Phase 3, C5).
 *
 * An open-but-checked value set, matching every other status column in `db/schema/clinical.ts`
 * (`problems.status`, `medications.status`) — validated at the application boundary rather than a
 * Postgres enum, which would need a migration every time a new status is needed.
 *
 * Pure and framework-free so `encounters.test.ts` can cover every transition with no database, per
 * `claude-kit/rules/invariants.md`'s "Business rules live in pure, tested modules".
 */

export const ENCOUNTER_STATUSES = ['scheduled', 'in_progress', 'completed', 'cancelled'] as const;
export type EncounterStatus = (typeof ENCOUNTER_STATUSES)[number];

export function isEncounterStatus(value: string): value is EncounterStatus {
  return (ENCOUNTER_STATUSES as readonly string[]).includes(value);
}

/**
 * Which status transitions are legal, keyed by the status a visit is currently in.
 *
 * `completed` and `cancelled` are terminal: an encounter route reopening either back to
 * `in_progress` would let a closed visit's clock keep moving after documentation against it may
 * already have been signed (`clinicalNotes.encounterId` references it). There is no path back to
 * `scheduled` from anywhere once a visit has actually started, for the same reason a clock does not
 * run backwards.
 */
const ALLOWED_TRANSITIONS: Record<EncounterStatus, readonly EncounterStatus[]> = {
  scheduled: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

/**
 * May a visit move from `from` to `to`?
 *
 * `from === to` is always legal — it is what an update that only sets `endedAt` and repeats the
 * current status looks like, and refusing a no-op status "change" would force every caller that
 * only wants to record an end time to also know and resend the current status.
 */
export function canTransitionEncounterStatus(from: EncounterStatus, to: EncounterStatus): boolean {
  if (from === to) return true;
  return ALLOWED_TRANSITIONS[from].includes(to);
}
